/**
 * Pure derivations behind the Buildings page: the table, the map and the
 * summary line. Only fields the Hub stores are used. The EPC class is
 * estimated from the performance score until certified EPCs (WP3 S3.2) are
 * connected, and is labelled "(est.)" wherever it is shown.
 */

/* ── EPC scale ───────────────────────────────────────────────── */
export const EPC_LADDER = [
  { rating: 'A+', color: '#2f7a3a' },
  { rating: 'A',  color: '#4f9a3e' },
  { rating: 'B',  color: '#8fbf3e' },
  { rating: 'C',  color: '#e3c238' },
  { rating: 'D',  color: '#e6963a' },
  { rating: 'E',  color: '#d97338' },
  { rating: 'F',  color: '#c95246' },
  { rating: 'G',  color: '#a43838' },
];

export const EPC_RATINGS = EPC_LADDER.map(({ rating }) => rating);

export const EPC_COLORS = Object.fromEntries(
  EPC_LADDER.map(({ rating, color }) => [rating, color])
);

export const pickEpc = (performance = 0) => {
  if (performance >= 90) return 'A+';
  if (performance >= 80) return 'A';
  if (performance >= 70) return 'B';
  if (performance >= 60) return 'C';
  if (performance >= 50) return 'D';
  if (performance >= 40) return 'E';
  if (performance >= 30) return 'F';
  return 'G';
};

/* ── Performance bands ───────────────────────────────────────── */
export const PERFORMANCE_BANDS = ['good', 'fair', 'poor'];

export const BAND_LABELS = { good: 'Good', fair: 'Fair', poor: 'Poor' };

export const pickBand = (performance = 0) => {
  const p = Number(performance) || 0;
  if (p >= 70) return 'good';
  if (p >= 50) return 'fair';
  return 'poor';
};

/**
 * Adds the display fields every view needs. The raw API fields are kept
 * untouched, so `performance == null` still renders as "—".
 */
export const decorateBuilding = (building) => {
  const performanceScore = Number(building.performance || 0);
  const hasCoords = Boolean(building.latitude && building.longitude);
  return {
    ...building,
    performanceScore,
    area: Number(building.total_area) || 0,
    epc: pickEpc(performanceScore),
    band: pickBand(performanceScore),
    displayAddress:
      [building.address, building.city, building.country].filter(Boolean).join(', ') ||
      building.location ||
      '',
    hasCoords,
    lat: hasCoords ? Number(building.latitude) : null,
    lng: hasCoords ? Number(building.longitude) : null,
  };
};

/* ── Filtering ───────────────────────────────────────────────── */
/**
 * `band` is 'all', a performance band, or 'attention' (fair + poor); `epc`
 * is a rating or null; `search` (name, address or city) is expected trimmed
 * and lower-cased.
 */
export const matchesFilter = (building, { band = 'all', epc = null, search = '' } = {}) => {
  if (band === 'attention' && building.band === 'good') return false;
  if (PERFORMANCE_BANDS.includes(band) && building.band !== band) return false;
  if (epc && building.epc !== epc) return false;
  if (!search) return true;
  return (
    String(building.name || '').toLowerCase().includes(search) ||
    String(building.displayAddress || building.city || '').toLowerCase().includes(search)
  );
};

export const countByBand = (buildings) =>
  buildings.reduce(
    (acc, building) => {
      acc[building.band] += 1;
      return acc;
    },
    { good: 0, fair: 0, poor: 0 }
  );

/* ── Summary line ────────────────────────────────────────────── */
export const summarizePortfolio = (buildings) => {
  const count = buildings.length;
  return {
    count,
    totalAreaM2: buildings.reduce((sum, b) => sum + b.area, 0),
    avgPerformance: count
      ? Math.round(buildings.reduce((sum, b) => sum + b.performanceScore, 0) / count)
      : null,
    needsAttention: buildings.filter((b) => b.band !== 'good').length,
    epcMix: EPC_LADDER
      .map((item) => ({ ...item, count: buildings.filter((b) => b.epc === item.rating).length }))
      .filter((item) => item.count > 0),
  };
};

/* ── Sorting ─────────────────────────────────────────────────── */
const EPC_RANK = Object.fromEntries(EPC_RATINGS.map((rating, index) => [rating, index]));

const SORT_VALUE = {
  name: (b) => b.name || '',
  city: (b) => b.city || '',
  type: (b) => b.building_type || b.type || '',
  area: (b) => b.area,
  performance: (b) => b.performanceScore,
  epc: (b) => EPC_RANK[b.epc],
};

export const SORT_KEYS = Object.keys(SORT_VALUE);

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/** Stable sort by one column; ties fall back to the name, A to Z. */
export const sortBuildings = (buildings, key = 'name', dir = 'asc') => {
  const value = SORT_VALUE[key] || SORT_VALUE.name;
  const sign = dir === 'desc' ? -1 : 1;
  return [...buildings].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const cmp = typeof x === 'number' && typeof y === 'number'
      ? x - y
      : collator.compare(String(x), String(y));
    return cmp !== 0 ? sign * cmp : collator.compare(a.name || '', b.name || '');
  });
};

/* ── CSV export ──────────────────────────────────────────────── */
const CSV_COLUMNS = [
  ['Name', (b) => b.name],
  ['Address', (b) => b.address || b.location],
  ['City', (b) => b.city],
  ['State / region', (b) => b.state],
  ['Postal code', (b) => b.zip_code],
  ['Country', (b) => b.country],
  ['Type', (b) => b.building_type || b.type],
  ['Year built', (b) => b.year_built],
  ['Floors', (b) => b.number_of_floors],
  ['Total area', (b) => b.total_area],
  ['Area unit', (b) => b.total_area_unit],
  ['Occupancy %', (b) => b.occupancy],
  ['Performance %', (b) => b.performance],
  ['Performance band', (b) => BAND_LABELS[b.band]],
  ['EPC (estimated)', (b) => b.epc],
  ['Latitude', (b) => b.latitude],
  ['Longitude', (b) => b.longitude],
];

const csvCell = (value) => {
  if (value == null) return '';
  let text = String(value);
  // A spreadsheet runs a cell that starts with = + - @ as a formula. Building
  // names are user input, so neutralise them; plain numbers stay numbers.
  if (/^[=+\-@\t\r]/.test(text) && Number.isNaN(Number(text))) {
    text = `'${text}`;
  }
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildingsToCsv = (buildings) =>
  [
    CSV_COLUMNS.map(([header]) => csvCell(header)).join(','),
    ...buildings.map((b) => CSV_COLUMNS.map(([, read]) => csvCell(read(b))).join(',')),
  ].join('\r\n');
